<template>
  <div>
    <div class="relative flex flex-col max-w-2xl min-h-screen mx-auto bg-white shadow">
      <!-- Loading -->
      <Loading v-if="loading" class="p-8" />

      <template v-else>
        <!-- Form -->
        <div>
          <div class="flex items-center justify-between bg-white border-b">
            <router-link :to="{ name: 'TrackingLinkStatus', params: { trackingCode: $route.params.trackingCode } }" class="flex items-center justify-center px-4 font-medium text-accent hover:underline" v-submitting="submitting">
              Cancel
            </router-link>
            <div class="p-4 font-medium leading-none text-center">
              Confirm Address
            </div>
            <button class="px-4 font-medium text-accent hover:underline" v-submitting="submitting" :disabled="!form.formatted_address" @click="updateAddress">
              Submit
            </button>
          </div>
          <div class="flex flex-col justify-center px-6 py-4 text-center text-gray-500">
            Search for your address and then drag the map so that the marker is directly where your order should go.
            <form @submit.prevent="updateAddress" class="py-4">
              <AddressAutocomplete
                ref="address"
                id="map"
                input-class="form-input"
                placeholder="Enter your address"
                :initial-value="order.formatted_address"
                @placechanged="getAddressData"
                @loaded="autocompleteLoaded" />
            </form>
          </div>
        </div>
        <!-- Map -->
        <div class="relative flex-grow w-full h-56">
          <div class="absolute top-0 left-0 z-10 w-full h-full bg-gray-400" v-if="!mapLoaded">
            <Loading />
          </div>
          <MglMap class="w-full h-full"
            ref="mapbox"
            :center="mapCenter" 
            :access-token="accessToken"
            :map-style="mapStyle"
            :zoom="zoom"
            :attribution-control="false"
            @move="mapMoved"
            @load="mapLoaded">
            <MglNavigationControl />
            <MglMarker :coordinates="markerCoordinates" anchor="bottom">
              <div slot="marker">
                <svg class="w-6 text-accent" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512"><path fill="currentColor" d="M172.268 501.67C26.97 291.031 0 269.413 0 192 0 85.961 85.961 0 192 0s192 85.961 192 192c0 77.413-26.97 99.031-172.268 309.67-9.535 13.774-29.93 13.773-39.464 0zM192 272c44.183 0 80-35.817 80-80s-35.817-80-80-80-80 35.817-80 80 35.817 80 80 80z"></path><circle cx="192" cy="192" fill="#fff" r="80" /></svg>
              </div>
            </MglMarker>
          </MglMap>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import AddressAutocomplete from '@/components/AddressAutocomplete'
import { MglMap, MglMarker, MglNavigationControl } from 'vue-mapbox'

export default {
  name: 'Validate',

  metaInfo: {
    title: 'Change Order Address'
  },

  components: {
    MglMap,
    MglMarker,
    MglNavigationControl,
    AddressAutocomplete
  },

  created () {
    this.fetchStatus()
  },

  data () {
    return {
      loading: true,
      mapLoading: true,
      submitting: false,
      order: {},
      form: {},
      mapCenter: [0, 0],
      markerCoordinates: [0, 0],
      orderCoordinates: [0, 0],
      accessToken: 'pk.eyJ1IjoiZmxlZXRiZWFjb24iLCJhIjoiY2puZXczdHhmMHFqeDNrbGZ3dGNmbGU2cCJ9.dZ0JMOD5222pAk-pR6wzYQ',
      mapStyle: 'mapbox://styles/fleetbeacon/ckcw7i47m15m71imu9z9329lr',
      zoom: 16
    }
  },

  methods: {
    autocompleteLoaded () {
      setTimeout(() => {
        this.$refs.address.$refs.address.focus()
      }, 50)
    },

    mapLoaded ()  {
      this.$refs.mapbox.map.flyTo(this.orderCoordinates)
      this.mapLoading = false
    },

    mapMoved (e) {
      let center = e.map.getCenter()
      this.markerCoordinates = [center.lng, center.lat]
      this.orderCoordinates = [center.lng, center.lat]
      this.form.latitude = center.lat
      this.form.longitude = center.lng
    },

    getAddressData ({ addressData, placeResultData }) {
      this.form = {
        formatted_address: placeResultData.formatted_address,
        street: placeResultData.formatted_address.split(',')[0] + ',' + placeResultData.formatted_address.split(',')[1],
        city: addressData.locality,
        zip: addressData.postal_code,
        latitude: parseFloat(addressData.latitude).toFixed(6),
        longitude: parseFloat(addressData.longitude).toFixed(6)
      }

      for (let component of placeResultData.address_components) {
        if (component.types.includes('country')) {
          this.form.country = component.short_name
        }
      }

      this.orderCoordinates = [this.form.longitude, this.form.latitude]
      this.$refs.mapbox.map.panTo(this.orderCoordinates)
    },

    async fetchStatus () {
      try {
        const { data } = await this.$api.get(`tracking-links/${this.$route.params.trackingCode}/status`)
        this.loading = false
        this.order = data.order
        this.form = data.order
        this.orderCoordinates = [parseFloat(data.order.longitude).toFixed(6), parseFloat(data.order.latitude).toFixed(6)]
        this.mapCenter = this.orderCoordinates
        this.markerCoordinates = this.orderCoordinates
      } catch (error) {
        console.error(error)
        this.$toasted.error(error.response.data.message || error)
      }
    },

    async updateAddress () {
      try {
        this.submitting = true
        await this.$api.post(`tracking-links/${this.$route.params.trackingCode}/address`, {
          formatted_address: this.form.formatted_address,
          street: this.form.street,
          city: this.form.city,
          zip: this.form.zip,
          country: this.form.country,
          longitude: this.form.longitude,
          latitude: this.form.latitude
        })
        this.$refs.address.$refs.address.clear()
        this.$toasted.show('Order address updated.')
        this.$router.push({ 
          name: 'TrackingLinkStatus', 
          params: { 
            trackingCode: this.$route.params.trackingCode 
          }
        })
      } catch (error) {
        if (response.status === 403) {
          this.$router.push({ 
            name: 'TrackingLinkStatus', 
            params: { 
              trackingCode: this.$route.params.trackingCode 
            } 
          })
        }  
        
        if (error.response.message) {
          this.$toasted.error(error.response.message)
        } else {
          throw Error(error)
        }
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>